import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Truck, Plus, Edit, Trash2, Phone, Mail, Download, Upload, Archive, ArchiveRestore } from 'lucide-react';
import { SortableHeader, applySorting } from '../components/SortableHeader';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const handleSort = (field, dir) => { setSortField(field); setSortDirection(dir); };
  const [formData, setFormData] = useState({
    name: '', contact_name: '', phone: '', email: '', address: '', order_days: []
  });

  useEffect(() => { fetchSuppliers(); }, [showArchived]);

  const fetchSuppliers = async () => {
    try {
      const params = showArchived ? { include_archived: true } : {};
      const response = await axios.get(`${API}/suppliers`, { withCredentials: true, params });
      setSuppliers(response.data);
    } catch { toast.error('Failed to load suppliers'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplier) {
        await axios.put(`${API}/suppliers/${editingSupplier.supplier_id}`, formData, { withCredentials: true });
        toast.success('Supplier updated');
      } else {
        await axios.post(`${API}/suppliers`, formData, { withCredentials: true });
        toast.success('Supplier created');
      }
      setIsModalOpen(false);
      setEditingSupplier(null);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save supplier');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', contact_name: '', phone: '', email: '', address: '', order_days: [] });
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name || '', contact_name: supplier.contact_name || '',
      phone: supplier.phone || '', email: supplier.email || '',
      address: supplier.address || '',
      order_days: supplier.order_days || []
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (supplierId) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) return;
    try {
      await axios.delete(`${API}/suppliers/${supplierId}`, { withCredentials: true });
      toast.success('Supplier deleted');
      fetchSuppliers();
    } catch { toast.error('Failed to delete supplier'); }
  };

  const handleArchive = async (supplier) => {
    const isArchived = supplier.archived;
    const action = isArchived ? 'unarchive' : 'archive';
    try {
      await axios.post(`${API}/suppliers/${supplier.supplier_id}/${action}`, {}, { withCredentials: true });
      toast.success(`Supplier ${action}d`);
      fetchSuppliers();
    } catch { toast.error(`Failed to ${action} supplier`); }
  };

  const toggleOrderDay = (day) => {
    setFormData(prev => ({
      ...prev,
      order_days: prev.order_days.includes(day)
        ? prev.order_days.filter(d => d !== day)
        : [...prev.order_days, day]
    }));
  };

  const openNewModal = () => { setEditingSupplier(null); resetForm(); setIsModalOpen(true); };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/suppliers/export/csv`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'suppliers.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Suppliers exported');
    } catch { toast.error('Failed to export'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/suppliers/import/csv`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(res.data.message);
      fetchSuppliers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Import failed'); }
    e.target.value = '';
  };

  const sortedSuppliers = applySorting(suppliers, sortField, sortDirection);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="suppliers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Suppliers
          </h1>
          <p className="text-muted-foreground mt-1">Manage your suppliers and order days</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex items-center gap-2 mr-2">
            <Switch
              checked={showArchived}
              onCheckedChange={setShowArchived}
              data-testid="show-archived-toggle"
            />
            <Label className="text-sm text-muted-foreground cursor-pointer" onClick={() => setShowArchived(!showArchived)}>
              Show Archived
            </Label>
          </div>
          <Button variant="outline" onClick={handleExport} data-testid="export-suppliers-btn">
            <Download className="w-4 h-4 mr-2" />Export
          </Button>
          <Button variant="outline" asChild data-testid="import-suppliers-btn">
            <label className="cursor-pointer">
              <Upload className="w-4 h-4 mr-2" />Import
              <input type="file" accept=".csv" onChange={handleImport} className="hidden" />
            </label>
          </Button>
          <Button onClick={openNewModal} data-testid="add-supplier-btn">
            <Plus className="w-4 h-4 mr-2" />Add Supplier
          </Button>
        </div>
      </div>

      {suppliers.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Truck className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold mb-2">No suppliers yet</h3>
              <p className="text-muted-foreground mb-4">Add your first supplier to get started</p>
              <Button onClick={openNewModal}><Plus className="w-4 h-4 mr-2" />Add Supplier</Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead><SortableHeader label="Supplier" field="name" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead><SortableHeader label="Contact" field="contact_name" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead>Order Days</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedSuppliers.map((supplier) => (
                  <TableRow
                    key={supplier.supplier_id}
                    className={supplier.archived ? 'opacity-50' : ''}
                    data-testid={`supplier-row-${supplier.supplier_id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div>
                          <p className="font-medium">{supplier.name}</p>
                          {supplier.address && (
                            <p className="text-sm text-muted-foreground">{supplier.address}</p>
                          )}
                        </div>
                        {supplier.archived && <Badge variant="outline" className="text-xs bg-muted">Archived</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {supplier.contact_name && <p className="text-sm">{supplier.contact_name}</p>}
                        {supplier.phone && (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Phone className="w-3 h-3" />{supplier.phone}
                          </div>
                        )}
                        {supplier.email && (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Mail className="w-3 h-3" />{supplier.email}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {supplier.order_days?.length > 0 ? (
                          supplier.order_days.map((day) => (
                            <Badge
                              key={day}
                              variant="secondary"
                              className="text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                              onClick={() => handleEdit(supplier)}
                              data-testid={`order-day-badge-${supplier.supplier_id}-${day}`}
                            >
                              {day.slice(0, 3)}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-sm text-muted-foreground">Any day</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => handleArchive(supplier)}
                          title={supplier.archived ? 'Unarchive' : 'Archive'}
                          data-testid={`archive-supplier-${supplier.supplier_id}`}
                        >
                          {supplier.archived
                            ? <ArchiveRestore className="w-4 h-4 text-green-600" />
                            : <Archive className="w-4 h-4 text-amber-600" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(supplier)} data-testid={`edit-supplier-${supplier.supplier_id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(supplier.supplier_id)} data-testid={`delete-supplier-${supplier.supplier_id}`}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Supplier Name *</Label>
                <Input id="name" value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Fresh Produce Co." required data-testid="supplier-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_name">Contact Name</Label>
                <Input id="contact_name" value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  placeholder="e.g., John Smith" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="e.g., +27 12 345 6789" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="e.g., orders@supplier.com" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Textarea id="address" value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Full address" rows={2} />
            </div>
            <div className="space-y-2">
              <Label>Order Days</Label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map((day) => (
                  <Button key={day} type="button"
                    variant={formData.order_days.includes(day) ? "default" : "outline"}
                    size="sm" onClick={() => toggleOrderDay(day)}>
                    {day.slice(0, 3)}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Days when orders can be placed with this supplier</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-supplier-btn">
                {editingSupplier ? 'Update' : 'Create'} Supplier
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Suppliers;
